import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Label,
  useToast,
} from '@ais-ui/ui-core';
import React, { useState } from 'react';
import { useDeleteTrack } from '@ais-ui/api';
import { mutateCallbacks } from '../../utils/mutateCallbacks';
import type { TrackResult } from '@ais-ui/api-spec/portserver';
import { useLocalization } from '@ais-ui/locale-management';

interface DeleteRouteProps {
  route: TrackResult;
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

export function DeleteRoute({
  route,
  showDialog,
  setShowDialog,
}: DeleteRouteProps) {
  const { messages } = useLocalization();
  const { mutate } = useDeleteTrack();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { toast } = useToast();

  const onDelete = (event: React.FormEvent) => {
    event.preventDefault();

    setIsSubmitting(true);

    const callBacks = mutateCallbacks(
      toast,
      messages,
      'delete',
      'route',
      setIsSubmitting,
      setShowDialog
    );

    if (!route.id) {
      toast({
        className: 'delete-route-error-toast',
        title: messages.genericToastTitleError,
        description: messages.routesDeleteErrorMessage,
        variant: 'destructive',
      });
      setShowDialog(false);
      setIsSubmitting(false);
      return;
    }

    mutate(route.id, callBacks);
  };

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent id="mutate-atpm-dialog">
        <DialogHeader>
          <DialogTitle>{messages.routesDeleteTitle}</DialogTitle>
          <DialogDescription>{messages.routesDeleteWarning}</DialogDescription>
        </DialogHeader>
        <form onSubmit={onDelete}>
          <div className="grid grid-cols-[auto_minmax(50%,_1fr)] 4 gap-x-2 items-baseline">
            <Label className="font-light">
              {messages.routesDeleteRouteName}
            </Label>
            <Label htmlFor="route-name">{route?.source?.ship?.name}</Label>
          </div>
          <DialogFooter className="p-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDialog(false)}
              disabled={isSubmitting}
            >
              {messages.genericCancel}
            </Button>
            <Button
              variant="destructive"
              id="route-confirm-delete-button"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? messages.genericDeleting
                : messages.genericDelete}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
